import React, { useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { getJournal } from "../../api/accountApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

/**
 * Direct port of digicampusbd's `general-accounts-report-journal` ("Journal
 * Transaction") — every voucher of every type (Receipt/Payment/Contra/
 * Journal) in a date range, ascending, each with its own debit/credit lines
 * and per-voucher totals. Confirmed via `GeneralAccountReportService
 * .findTransactionJournal` that the old app does no aggregation at all here
 * — it's a raw voucher listing. Reuses the existing `VoucherResponse` shape
 * (same one `listVouchers` already returns) via a new `GET
 * /api/accounts/vouchers/reports/journal` endpoint (no trnType filter, all
 * types, ascending order) — per-voucher totals are summed client-side from
 * its own lines.
 */
export default function JournalTransactionPage() {
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [vouchers, setVouchers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    setError("");
    setLoading(true);
    try {
      setVouchers(await getJournal(fromDate, toDate));
    } catch (err) {
      setError("Failed to load journal.");
    } finally {
      setLoading(false);
    }
  };

  const grandDebit = (vouchers || []).reduce((sum, v) => sum + (v.lines || []).reduce((s, l) => s + l.debitAmount, 0), 0);
  const grandCredit = (vouchers || []).reduce((sum, v) => sum + (v.lines || []).reduce((s, l) => s + l.creditAmount, 0), 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Journal Transaction</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          From Date
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To Date
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        vouchers && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Voucher No</th>
                  <th>Type</th>
                  <th>Note</th>
                  <th>Ledger Lines (Debit / Credit)</th>
                  <th>Total Debit</th>
                  <th>Total Credit</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((v) => {
                  const totalDebit = (v.lines || []).reduce((s, l) => s + l.debitAmount, 0);
                  const totalCredit = (v.lines || []).reduce((s, l) => s + l.creditAmount, 0);
                  return (
                    <tr key={v.trnId}>
                      <td>{v.trnDate}</td>
                      <td>{v.voucherNo}</td>
                      <td>{v.trnType}</td>
                      <td>{v.voucherNote}</td>
                      <td>
                        {(v.lines || [])
                          .map((l) => `${l.ledgerName}: ${l.debitAmount ? "Dr " + l.debitAmount.toFixed(2) : "Cr " + l.creditAmount.toFixed(2)}`)
                          .join("; ")}
                      </td>
                      <td>{totalDebit.toFixed(2)}</td>
                      <td>{totalCredit.toFixed(2)}</td>
                    </tr>
                  );
                })}
                {vouchers.length === 0 && (
                  <tr>
                    <td colSpan={7}>No vouchers found for this selection.</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ fontWeight: 800, textAlign: "right" }}>
                    Grand Total
                  </td>
                  <td>
                    <strong>{grandDebit.toFixed(2)}</strong>
                  </td>
                  <td>
                    <strong>{grandCredit.toFixed(2)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
